import React, { useState } from 'react';
import { mapGoogleAuthError, startGoogleSignIn } from '../../utils/dashboardGoogleAuth';
import { mapEmailAuthError, startEmailSignIn } from '../../utils/dashboardEmailAuth';
import { sendPasswordResetEmail as sendPasswordResetEmailSes } from '../../services/emailService';
import { onSoftButtonHover, softButtonStyle } from '../../styles/softButtons';

interface LoginPageProps {
  onGoToRegister: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  backgroundColor: '#475569',
  border: '1px solid #64748b',
  borderRadius: '8px',
  color: 'white',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  color: '#e2e8f0',
  fontSize: '14px',
  display: 'block',
  marginBottom: '8px',
};

export default function LoginPage({ onGoToRegister }: LoginPageProps) {
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleGoogleLogin = async () => {
    setError('');
    setInfo('');
    setGoogleLoading(true);

    try {
      const result = await startGoogleSignIn();
      if (!result.ok) {
        setError(result.error);
      }
    } catch (err: unknown) {
      console.error('Google login error:', err);
      setError(mapGoogleAuthError(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setEmailLoading(true);

    try {
      const result = await startEmailSignIn(email, password);
      if (!result.ok) {
        setError(result.error);
      }
    } catch (err: unknown) {
      console.error('Email login error:', err);
      setError(mapEmailAuthError(err));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Enter your email above, then click Forgot password.');
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmailSes({ email: trimmed });
      setInfo('Password reset email sent. Check your inbox (and spam folder).');
    } catch (err: unknown) {
      console.error('Password reset error:', err);
      setError(mapEmailAuthError(err));
    } finally {
      setResetLoading(false);
    }
  };

  const busy = googleLoading || emailLoading || resetLoading;

  return (
    <div
      className="dash-login-shell"
      style={{
        minHeight: '100dvh',
        backgroundColor: '#1e293b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        className="dash-login-card"
        style={{
          backgroundColor: '#334155',
          padding: '40px',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              boxShadow: '0 4px 14px rgba(249, 115, 22, 0.35)',
            }}
          >
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: '20px' }}>W2P</span>
          </div>
          <h1 style={{ color: 'white', fontSize: '24px', margin: 0 }}>Web2POS</h1>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Sign in to your account</p>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '20px',
            }}
          >
            <p style={{ color: '#fca5a5', margin: 0, fontSize: '14px', whiteSpace: 'pre-line' }}>{error}</p>
          </div>
        )}

        {info && (
          <div
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.12)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '20px',
            }}
          >
            <p style={{ color: '#86efac', margin: 0, fontSize: '14px' }}>{info}</p>
          </div>
        )}

        <form onSubmit={handleEmailLogin}>
          <div style={{ marginBottom: '16px' }}>
            <label htmlFor="login-email" style={labelStyle}>
              Email
            </label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              required
              disabled={busy}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '8px' }}>
            <label htmlFor="login-password" style={labelStyle}>
              Password
            </label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={busy}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '20px', textAlign: 'right' }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={busy}
              style={{
                background: 'none',
                border: 'none',
                color: '#fb923c',
                cursor: busy ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                padding: 0,
                opacity: busy ? 0.6 : 1,
              }}
            >
              {resetLoading ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>

          <button
            type="submit"
            disabled={busy}
            style={softButtonStyle({ disabled: busy, variant: 'primary' })}
            onMouseEnter={(e) => onSoftButtonHover(e, true)}
            onMouseLeave={(e) => onSoftButtonHover(e, false)}
          >
            {emailLoading ? 'Signing in…' : 'Sign in with email'}
          </button>
        </form>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: '24px 0',
          }}
        >
          <div style={{ flex: 1, height: 1, backgroundColor: '#64748b' }} />
          <span style={{ color: '#94a3b8', fontSize: '13px' }}>or</span>
          <div style={{ flex: 1, height: 1, backgroundColor: '#64748b' }} />
        </div>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={busy}
          style={softButtonStyle({ disabled: busy, variant: 'google' })}
          onMouseEnter={(e) => onSoftButtonHover(e, true)}
          onMouseLeave={(e) => onSoftButtonHover(e, false)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {googleLoading ? 'Signing in with Google…' : 'Continue with Google'}
        </button>

        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '16px', fontSize: '12px', lineHeight: 1.5 }}>
          Invited with a custom email? Use the invite link to set your password, then sign in above.
        </p>

        <p style={{ textAlign: 'center', color: '#94a3b8', marginTop: '16px', fontSize: '14px' }}>
          Don&apos;t have an account?{' '}
          <button
            type="button"
            onClick={onGoToRegister}
            style={{
              background: 'none',
              border: 'none',
              color: '#fb923c',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'underline',
              padding: 0,
            }}
          >
            Sign up
          </button>
        </p>

        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '24px', fontSize: '12px' }}>
          © TheZoneOrder
        </p>
      </div>
    </div>
  );
}
